import { KnownCourierStatus } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback, useMemo } from 'react';
import { useMutationDeactivateIncompleteCourier, useMutationEditCourier } from '../../couriers.queries';
import { CourierDetailsActions } from '../actions/courier-details.actions';

/**
 * Activate and deactivate have no modal, so they run straight off the action. Only the status is
 * sent: a courier's access level is left alone, so reactivating restores them exactly as they were.
 *
 * Incomplete couriers can only be deactivated, which goes through the users endpoint.
 */
export function useCourierStatusChange(
  setAction: (action: CourierDetailsActions | null) => void,
  courierID: string,
  isIncomplete: boolean
) {
  const { mutate: editCourier } = useMutationEditCourier(courierID);
  const { mutate: deactivateIncompleteCourier } = useMutationDeactivateIncompleteCourier(courierID);

  const callbacks = useMemo(
    () => ({
      onSuccess: () => setAction(CourierDetailsActions.STATUS_CHANGE_SUCCESS),
      onError: () => setAction(CourierDetailsActions.STATUS_CHANGE_ERROR)
    }),
    [setAction]
  );

  const activateCourier = useCallback(() => {
    // Reactivation is never offered for an incomplete courier.
    if (isIncomplete) {
      return;
    }

    editCourier({ status: KnownCourierStatus.Active }, callbacks);
  }, [isIncomplete, editCourier, callbacks]);

  const deactivateCourier = useCallback(() => {
    if (isIncomplete) {
      deactivateIncompleteCourier(undefined, callbacks);
    } else {
      editCourier({ status: KnownCourierStatus.Inactive }, callbacks);
    }
  }, [isIncomplete, deactivateIncompleteCourier, editCourier, callbacks]);

  return { activateCourier, deactivateCourier };
}
